<template>
    <div class="e-checkbox-wrapper">
        <Checkbox
            v-bind="primeVueProps"
            :id="checkboxId"
            :binary="$attrs['native-value'] === undefined"
            :value="$attrs['native-value']"
            @update:modelValue="$emit('update:modelValue', $event)"
        />
        <label v-if="$slots.default" :for="checkboxId" class="e-checkbox-label">
            <slot />
        </label>
    </div>
</template>

<script>
import Checkbox from 'primevue/checkbox';
import { getCurrentInstance } from 'vue';
/**
 * ECheckbox - Abstraction wrapper around PrimeVue's Checkbox
 *
 * Usage:
 *   <e-checkbox v-model="agreed">
 *     I agree to the terms and conditions
 *   </e-checkbox>
 *
 *   <e-checkbox v-model="notifications" native-value="email">
 *     Email notifications
 *   </e-checkbox>
 *
 *   <e-checkbox v-model="isChecked" :force-prime-vue="true">
 *     Force PrimeVue checkbox
 *   </e-checkbox>
 *
 * All Buefy b-checkbox props, events, and slots are supported.
 *
 */
export default {
    name: 'ECheckbox',
    components: {
        Checkbox,
    },
    inheritAttrs: false,
    computed: {
        primeVueProps() {
            const props = { ...this.$attrs };

            // Remove Buefy-specific props that don't apply to PrimeVue
            delete props['native-value'];
            delete props.type; // Buefy uses type for styling

            return props;
        },
        checkboxId() {
            // Generate a unique ID for the checkbox-label association
            return `e-checkbox-${getCurrentInstance().uid}`;
        },
    },
    emits: ['update:modelValue'],
};
</script>

<style scoped>
.e-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
}

/* PrimeVue checkbox root (first child) — nudge to mid-line with label text */
.e-checkbox-wrapper > :deep(div) {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    line-height: 1;
    margin-top: 0.1875rem;
}

.e-checkbox-label {
    cursor: pointer;
    flex: 1 1 auto;
    line-height: 1.425;
    margin: 0;
    min-width: 0;
    padding-top: 0.0625rem;
    user-select: none;
}
</style>
